// Components==============
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import storeWrongAnswer from 'actions/wrongAnswer/store';
import {
    highlightChars,
    HighlightExerciseContext,
} from 'components/Exercises/HighlightExercise/HighlightExercise';
import { motion } from 'framer-motion';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { HomeworkContext } from 'screens/Homework/Homework';
import { useAudio } from 'utilities/useAudio';
import { getHighlightBackground } from '../../utils/highlightString';
import { stripHighlightChars } from '../../utils/stripHighlightChars';
import useWrongAnimation from '../../utils/useWrongAnimation';
import './Word.module.scss';
// =========================

const Letter = ({
    word,
    letter,
    correctLetterIndexes,
    index,
    selectedCharacters,
    setSelectedCharacters,
    wordLength,
}: {
    word: string;
    letter: string;
    correctLetterIndexes: number[];
    index: number;
    wordLength: number;
    selectedCharacters: number[];
    setSelectedCharacters: React.Dispatch<React.SetStateAction<number[]>>;
}) => {
    const { sequence } = useContext(HomeworkContext);

    const { wrong, setWrong } = useWrongAnimation();

    const dispatch = useDispatch();

    const { homeworkId, treatmentId } = useParams() as {
        homeworkId: string;
        treatmentId: string;
    };

    if (highlightChars.includes(letter as any)) return null;

    const isAnsweredCorrect = selectedCharacters.includes(index);

    // retrieve special character associated with letter
    const characterBeforeBeforeLetter = word[index - 2];
    const characterBeforeLetter = word[index - 1];
    const characterAfterLetter = word[index + 1];
    const characterAfterAfterLetter = word[index + 2];
    const specialChar =
        [
            characterBeforeLetter,
            characterAfterLetter,
            characterBeforeBeforeLetter,
            characterAfterAfterLetter,
        ].find((char) => highlightChars.includes(char as any)) || '';

    const backgroundColor = isAnsweredCorrect
        ? getHighlightBackground(specialChar)
        : '#cfe1fa';

    const variants = {
        initial: {
            opacity: 0,
        },
        whileHover: {
            opacity: 1,
        },
        wrong: {
            x: [0, -4, 4, -4, 0],
            opacity: 0,
        },
        right: {
            opacity: 1,
        },
    };

    function handleClick() {
        if (correctLetterIndexes.includes(index)) {
            if (isAnsweredCorrect) return;

            return setSelectedCharacters((prev) => [...prev, index]);
        }

        // prevent double click
        if (wrong) return;

        setWrong(true);

        dispatch(
            storeWrongAnswer({
                given_answer: 'verkeerd geklikt',
                correct_answer: letter,
                type: 'highlight',
                metadata: { sequence },
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
        );
    }

    return (
        <motion.span
            initial="initial"
            animate={wrong ? 'wrong' : isAnsweredCorrect ? 'right' : 'initial'}
            whileHover="whileHover"
            styleName={`letter ${isAnsweredCorrect ? 'answered' : ''} ${
                wordLength > 8 && wordLength < 13 ? `length-${wordLength}` : ''
            } ${wordLength >= 13 ? 'length-greater' : ''} ${
                specialChar === '~' ? 'black-text' : ''
            } `}
            onClick={handleClick}
        >
            <motion.div
                styleName="block"
                variants={variants}
                transition={{ duration: 0.4 }}
                style={{ backgroundColor }}
            />
            {letter}
            <wbr />
        </motion.span>
    );
};

export default function Word() {
    const [selectedCharacters, setSelectedCharacters] = useState<number[]>([]);

    const {
        sentences,
        selected,
        setSelected,
        setAnsweredSentences,
        paragraphId,
        setTextToBeRead,
        answeredSentences,
    } = useContext(HighlightExerciseContext);

    const word = sentences[selected.index].sentence
        .split(' ')
        [selected.wordIndex].replace('.', '');

    const wordSplitUpInLetters = word.split('');

    const correctLetterIndexes = useMemo(() => {
        const indexOfHighlighCharacters = word
            .split('')
            .map((l, i) => (highlightChars.includes(l as any) ? i : undefined))
            .filter((exists) => exists !== undefined) as number[];

        // wrap every pair of indexOfHighlightCharacters in a array
        const pairsOfHighlightCharacters = indexOfHighlighCharacters.reduce(
            (acc: number[][], curr, i, arr: number[]) => {
                if (i % 2 === 0) {
                    acc.push(arr.slice(i, i + 2));
                }

                return acc;
            },
            []
        );

        const indexOfLettersInBetween = pairsOfHighlightCharacters.reduce(
            (acc: number[], curr) => {
                const [start, end] = curr;

                for (let i = start + 1; i < end; i++) {
                    acc.push(i);
                }

                return acc;
            },
            []
        );

        return indexOfLettersInBetween;
    }, []);

    useEffect(() => {
        // Check if all letters are selected
        if (selectedCharacters.length !== correctLetterIndexes.length) return;

        function handleFinished() {
            // Reset selected state to go back to sentence overview
            setSelected({
                id: '',
                index: -1,
                wordIndex: -1,
                type: 'sentences',
            });

            const { sentence } = sentences[selected.index];
            const wordsThatNeedHighlight = sentence
                .split(' ')
                .filter((w) =>
                    w
                        .split('')
                        .find((letter) =>
                            highlightChars.includes(letter as any)
                        )
                );

            const answeredSentence = answeredSentences.find(
                (as) => as.id === selected.id
            );

            const answeredWords = answeredSentence?.words || [];

            const allWordsAreHighlighted =
                wordsThatNeedHighlight.length === answeredWords.length + 1;

            // If the sentence already exists, update
            if (answeredSentence)
                return setAnsweredSentences((prev) =>
                    prev.map((sent) =>
                        answeredSentence.id === sent.id
                            ? {
                                  ...sent,
                                  status: allWordsAreHighlighted
                                      ? 'correct'
                                      : 'answered',
                                  words: [...sent.words, selected.wordIndex],
                              }
                            : sent
                    )
                );

            // if the sentence does not exist, add it
            setAnsweredSentences((prev) => [
                ...prev,
                {
                    id: selected.id,
                    paragraphId,
                    status: allWordsAreHighlighted ? 'correct' : 'answered',
                    words: [selected.wordIndex],
                },
            ]);
        }

        const timeout = setTimeout(handleFinished, 1250);

        return () => clearTimeout(timeout);
    }, [selectedCharacters]);

    const readableWord = stripHighlightChars(word);
    const { play, url } = useAudio({ text: readableWord });

    useEffect(() => {
        if (!url) return;
        setTextToBeRead(readableWord);
        play();
    }, [url]);

    return (
        <div
            styleName={`word ${
                readableWord.length > 8 ? 'adjust-padding' : ''
            }`}
        >
            {wordSplitUpInLetters.map((letter, i) => (
                <Letter
                    word={word}
                    letter={letter}
                    index={i}
                    correctLetterIndexes={correctLetterIndexes}
                    key={i}
                    selectedCharacters={selectedCharacters}
                    setSelectedCharacters={setSelectedCharacters}
                    wordLength={readableWord.length}
                />
            ))}
            {selectedCharacters.length === correctLetterIndexes.length && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    styleName="done"
                >
                    <FontAwesomeIcon icon={faCheck} />
                </motion.div>
            )}
        </div>
    );
}
